import { Alert, Button, Checkbox, Divider, Form, Input, InputNumber, Modal, Radio, Select, Spin, Switch, Tag, Tooltip, TreeSelect, Upload } from 'antd';
import { useForm } from 'antd/lib/form/Form';
import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router';
import dspApi from './DspApi';
import { DspBean, TimeZoneBean, CountryBean, ClusterKeyValue, DictionaryOption, IabCategoryBean } from '../../../api/api';
import { formDataLoader, FormDataType } from '../../../util/FormDataLoader';
import { FEE_CONFIG_DSP, getIabCategoriesAsTree, MANDATORY_REPORT_COLUMNS, resolveIabDefaultsChange, truncateErrorMessage } from '../../../util/Util';
import { ArrowLeftOutlined, EyeOutlined, InfoCircleOutlined, UploadOutlined } from '@ant-design/icons';
import ChangeLogHistoryModal from '../changeHistory/changeLogHistoryModal';
import { DSP_EXCLUDED_FIELDS, DSP_FIELD_LABELS } from '../../../util/changeLogFieldLabels';
import DspTabs, { DspTabKey, dspTabs } from './DspTabs';
import { BasicDetails, DashboardConfigure, FilteringOptions } from './DspTabSections';
import './style.css';
import DomainListCard from './DomainListCard';

const FormItem = Form.Item;
const Option = Select.Option

const formInfoItems: FormDataType[] = [
    FormDataType.TimeZones,
    FormDataType.Countries,
    FormDataType.ClusterEndpoints,
    FormDataType.TargetDeviceTypes,
    FormDataType.TargetOperatingSystems,
    FormDataType.IabCategories,
]

interface Props {
    miniExchange?: boolean
}

const WinNotificationEnum = DspBean.WinNotificationEnum

const DspEdit: React.FC<Props> = ({ miniExchange }) => {

    const [info, setInfo] = useState<{
        timezones: TimeZoneBean[];
        countries: CountryBean[];
        targetDeviceTypes: DictionaryOption[];
        targetOperatingSystems: DictionaryOption[];
        iabCategories: IabCategoryBean[];
    }>({
        timezones: [],
        countries: [],
        targetDeviceTypes: [],
        targetOperatingSystems: [],
        iabCategories: [],
    })

    const [form] = useForm()
    const [error, setError] = useState()
    const [pageLoading, setPageLoading] = useState(true)
    const [validation, setValidation] = useState(true);
    const history = useHistory()
    const { id } = useParams<{ id?: string }>()

    const [activeTab, setActiveTab] = useState<DspTabKey>('basic')
    const iabPrevRef = useRef<number[]>([])
    const [environmentType, setEnvironmentType] = useState<string[]>(['APP', 'WEB'])
    const [environmentError, setEnvironmentError] = useState('')
    const [clusters, setClusters] = useState<ClusterKeyValue[]>([])
    const [defaultCluster, setDefaultCluster] = useState<string>('')
    const [clusterUrlValues, setClusterUrlValues] = useState<Record<string, string>>({})

    // ─── Domains state ──────────────────────────────────────────────────────
    const [domains, setDomains] = useState<string[]>([])
    const [acceptAllDomains, setAcceptAllDomains] = useState(false)
    const [blackListDomains, setBlackListDomains] = useState(false)
    const [domainsCollapsed, setDomainsCollapsed] = useState(false)
    const [domainsFileLabel, setDomainsFileLabel] = useState<string | null>(null)

    // ─── Mobile App IDs state ───────────────────────────────────────────────
    const [mobileAppIds, setMobileAppIds] = useState<string[]>([])
    const [acceptAllMobileAppIds, setAcceptAllMobileAppIds] = useState(false)
    const [blackListMobileAppIds, setBlackListMobileAppIds] = useState(false)
    const [mobileAppsCollapsed, setMobileAppsCollapsed] = useState(false)
    const [mobileAppIdsFileLabel, setMobileAppIdsFileLabel] = useState<string | null>(null)

    const [dashboardKpi, setDashboardKpi] = useState<string[]>([])
    const [reportColumns, setReportColumns] = useState<string[]>([])
    const [apiAccess, setApiAccess] = useState<boolean>(true)
    const [apiUrl, setApiUrl] = useState<string>('')
    const [apiKey, setApiKey] = useState<string>('')
    const [kpiOptions, setKpiOptions] = useState<{value: string, label: string}[]>([])
    const [allReportColumnOptions, setAllReportColumnOptions] = useState<{value: string, label: string}[]>([])
    const [kpiMetric, setKpiMetric] = useState<'Impression' | 'Wins'>('Impression')

    const handleClusterUrlChange = (key: string, value: string) => {
        const updated = { ...clusterUrlValues, [key]: value }
        setClusterUrlValues(updated)
        if (!defaultCluster && value) {
            setDefaultCluster(key)
        } else if (defaultCluster === key && !value) {
            const nextFilled = clusters.find(c => c.key !== key && updated[c.key])
            setDefaultCluster(nextFilled?.key ?? '')
        }
    }

    const onChangeIab = (value: number[]) => {
        const next = resolveIabDefaultsChange(value || [], iabPrevRef.current, info.iabCategories as any)
        iabPrevRef.current = next
        form.setFieldsValue({ iabCategoryIds: next })
        return next
    }

    const navigateToDspList = () => {
        history.push(miniExchange ? '/mini-exchange' : '/dsp', { fromDspList: true });
    };

    const handleTargetCountrySelect = (value: string) => {
        const allIso3 = (info.countries ?? [])
            .map((c) => c.iso3)
            .filter((v): v is string => Boolean(v))
        if (value === 'All') {
            form.setFieldsValue({ targetCountries: ['All', ...allIso3] })
        } else {
            const current: string[] = form.getFieldValue('targetCountries') ?? []
            const selectedCountries = current.filter((v) => v !== 'All')
            if (selectedCountries.length === allIso3.length) {
                form.setFieldsValue({ targetCountries: ['All', ...selectedCountries] })
            }
        }
    }

    const handleTargetCountryDeselect = (value: string) => {
        if (value === 'All') {
            form.setFieldsValue({ targetCountries: [] })
        } else {
            const current: string[] = form.getFieldValue('targetCountries') ?? []
            form.setFieldsValue({ targetCountries: current.filter((v) => v !== 'All' && v !== value) })
        }
    }

    const handleAllOptionSelect = (fieldName: string, allOptions: string[]) => (value: string) => {
        if (value === 'All') {
            form.setFieldsValue({ [fieldName]: ['All', ...allOptions] })
        } else {
            const current: string[] = form.getFieldValue(fieldName) ?? []
            const selected = current.filter((v) => v !== 'All')
            if (selected.length === allOptions.length) {
                form.setFieldsValue({ [fieldName]: ['All', ...selected] })
            }
        }
    }

    const handleAllOptionDeselect = (fieldName: string) => (value: string) => {
        if (value === 'All') {
            form.setFieldsValue({ [fieldName]: [] })
        } else {
            const current: string[] = form.getFieldValue(fieldName) ?? []
            form.setFieldsValue({ [fieldName]: current.filter((v) => v !== 'All' && v !== value) })
        }
    }

    const handleSubmit = () => {
        if (environmentType.length === 0) {
            setEnvironmentError('Please select at least one environment')
            if (activeTab !== 'filtering') setActiveTab('filtering')
            return
        }
        setEnvironmentError('')
        const radioValue = kpiMetric === 'Impression' ? 'Impressions' : 'Wins'
        form.validateFields().then(values => {
            const targetCountries: string[] = values?.targetCountries ?? []
            const deviceTargeting: string[] = values?.targetDeviceTypes ?? []
            const osTargeting: string[] = values?.targetOperatingSystems ?? []
            const environment = environmentType.length === 2 ? 'BOTH' : (environmentType[0] ?? 'BOTH')
            const clusterEndpoints = clusters
                .filter(c => values[`cluster_${c.key}`])
                .map(c => ({
                    cluster: c.key,
                    endpointUrl: values[`cluster_${c.key}`],
                    defaultEndpoint: defaultCluster === c.key,
                }))
            const selectedIabIds: number[] = values?.iabCategoryIds ?? []
            const codeByIabId = new Map(info.iabCategories.map(c => [c.id, c.code]))
            const allowedIabCategoryCodes = selectedIabIds
                .map(id => codeByIabId.get(id))
                .filter((code): code is string => Boolean(code))
            const payload = {
                ...values,
                clusterEndpoints,
                targetCountries: targetCountries.includes('All') ? ['All'] : targetCountries,
                targetDeviceTypes: deviceTargeting.includes('All') ? ['All'] : deviceTargeting,
                targetOperatingSystems: osTargeting.includes('All') ? ['All'] : osTargeting,
                domains: acceptAllDomains ? [] : domains,
                acceptAllDomains,
                blackListDomains,
                mobileAppIds: acceptAllMobileAppIds ? [] : mobileAppIds,
                acceptAllMobileAppIds,
                blackListMobileAppIds,
                environment,
                dashboardConfig: {
                    reportColumns: [...new Set([...reportColumns, radioValue])],
                    kpiMetrics: [...new Set([...dashboardKpi, radioValue])],
                    apiAccessEnabled: apiAccess,
                },
                allowedIabCategoryCodes,
            };
            payload.fee = payload.dspFee;
            delete payload.dspFee;
            delete payload.iabCategoryIds;
            clusters.forEach(c => delete payload[`cluster_${c.key}`]);

            dspApi
                .edit(Number.parseInt(id ?? '-1'), payload)
                .then(navigateToDspList)
                .catch(err => {
                    setError(truncateErrorMessage(err?.error))
                })
        }).catch(() => {
            if (activeTab !== 'basic') setActiveTab('basic')
        })
    }

    const handleGenerateApi = async () => {
        try {
            const result = await dspApi.getDashboardApiKey(Number(id))
            if (result?.data?.externalReportUrl) setApiUrl(result.data.externalReportUrl)
            if (result?.data?.apiKey) setApiKey(result.data.apiKey)
        } catch (e) {
            setError(truncateErrorMessage((e as any)?.error))
        }
    }

    const loadData = async () => {
        setPageLoading(true)
        try {
            const [rawResponse, formInfo, dashboardOptions, dashboardConfig] = await Promise.all([
                dspApi.view(id) as Promise<any>,
                formDataLoader(...formInfoItems) as Promise<any>,
                dspApi.getDashboardConfigOptions().catch(() => null),
                dspApi.getDashboardConfig(Number(id)).catch(() => null),
            ])
            const response: any = rawResponse
            setInfo(formInfo as any)
            if (Array.isArray(formInfo.clusterEndpoints) && formInfo.clusterEndpoints.length > 0) {
                setClusters(formInfo.clusterEndpoints)
            } else {
                setError('Failed to load cluster endpoints. Please refresh the page.' as any)
            }

            // Populate dashboard options
            const optData = dashboardOptions?.data
            if (Array.isArray(optData?.kpiMetrics) && optData.kpiMetrics.length > 0) {
                setKpiOptions(optData.kpiMetrics.filter((o: {value: string}) => o.value !== 'Impressions' && o.value !== 'Wins'))
            }
            if (Array.isArray(optData?.reportColumns) && optData.reportColumns.length > 0) {
                setAllReportColumnOptions(optData.reportColumns.filter((o: {value: string}) => o.value !== 'Impressions' && o.value !== 'Wins'))
            }

            // Populate saved dashboard config
            const cfg = dashboardConfig?.data
            if (cfg) {
                if (Array.isArray(cfg.kpiMetrics)) {
                    setDashboardKpi(cfg.kpiMetrics.filter((v: string) => v !== 'Impressions' && v !== 'Wins'))
                    setKpiMetric(cfg.kpiMetrics.includes('Wins') && !cfg.kpiMetrics.includes('Impressions') ? 'Wins' : 'Impression')
                }
                if (Array.isArray(cfg.reportColumns)) setReportColumns(cfg.reportColumns.filter((v: string) => v !== 'Impressions' && v !== 'Wins'))
                if (typeof cfg.apiAccessEnabled === 'boolean') setApiAccess(cfg.apiAccessEnabled)
                if (cfg.externalReportUrl) setApiUrl(cfg.externalReportUrl)
                if (cfg.apiKey) setApiKey(cfg.apiKey)
            }

            const allIso3 = (formInfo as any)?.countries?.map((c: CountryBean) => c.iso3).filter(Boolean) ?? []
            const apiTargetCountries: string[] | undefined = response?.data?.targetCountries
            const derivedTargetCountries =
                apiTargetCountries?.includes('All')
                    ? ['All', ...allIso3]
                    : (apiTargetCountries ??
                        response.data.countries?.map((country: CountryBean) => country.iso3).filter(Boolean) ??
                        [])

            const apiDeviceTargeting: string[] | undefined = response?.data?.targetDeviceTypes
            const derivedDeviceTargeting = apiDeviceTargeting?.includes('All')
                ? ['All', ...(formInfo.targetDeviceTypes ?? []).map((o) => o.value)]
                : (apiDeviceTargeting ?? [])

            const apiOsTargeting: string[] | undefined = response?.data?.targetOperatingSystems
            const derivedOsTargeting = apiOsTargeting?.includes('All')
                ? ['All', ...(formInfo.targetOperatingSystems ?? []).map((o) => o.value)]
                : (apiOsTargeting ?? [])

            // Map the saved category codes back to ids for the TreeSelect (field holds ids).
            const idByIabCode = new Map((formInfo.iabCategories ?? []).map((c) => [c.code, c.id]))
            const derivedIabCategoryIds = (response?.data?.allowedIabCategoryCodes ?? [])
                .map((code: string) => idByIabCode.get(code))
                .filter((iabId: number | undefined): iabId is number => iabId != null)

            const data = {
                ...response.data,
                targetCountries: derivedTargetCountries,
                targetDeviceTypes: derivedDeviceTargeting,
                targetOperatingSystems: derivedOsTargeting,
                iabCategoryIds: derivedIabCategoryIds,
            };
            if (Array.isArray(data.fee)) {
                const dspFee = FEE_CONFIG_DSP?.map((config, index) => {
                    const match = data?.fee?.find(f => f?.mediaType === config?.mediaType);
                    return { id: match?.id, mediaType: config?.mediaType, fee: match ? match?.fee : 0 };
                });
                delete data.fee;
                data.dspFee = dspFee;
            }
            form.setFieldsValue(data)
            setValidation(response.data.validation)

            const savedClusters: any[] = response.data?.clusterEndpoints ?? []
            if (savedClusters.length > 0) {
                const clusterFormValues: Record<string, string> = {}
                const urlValues: Record<string, string> = {}
                savedClusters.forEach((c: any) => {
                    const clusterKey = typeof c.cluster === 'object' ? c.cluster?.key : c.cluster
                    clusterFormValues[`cluster_${clusterKey}`] = c.endpointUrl
                    urlValues[clusterKey] = c.endpointUrl
                })
                form.setFieldsValue(clusterFormValues)
                setClusterUrlValues(urlValues)
                const defaultC = savedClusters.find((c: any) => c.defaultEndpoint)
                const rawKey = defaultC?.cluster ?? savedClusters[0].cluster
                setDefaultCluster(typeof rawKey === 'object' ? rawKey?.key : rawKey)
            }

            // Pre-populate flat domains from new API shape
            if (Array.isArray(response.data?.domains) && response.data.domains.length > 0) {
                const loadedDomains = response.data.domains
                setDomains(loadedDomains)
                setDomainsFileLabel('domains.csv')
            }
            if (response.data?.acceptAllDomains) setAcceptAllDomains(true)
            if (response.data?.blackListDomains) setBlackListDomains(true)

            // Pre-populate flat mobileAppIds from new API shape
            if (Array.isArray(response.data?.mobileAppIds) && response.data.mobileAppIds.length > 0) {
                const loadedMobileAppIds = response.data.mobileAppIds
                setMobileAppIds(loadedMobileAppIds)
                setMobileAppIdsFileLabel('mobileAppIds.csv')
            }
            if (response.data?.acceptAllMobileAppIds) setAcceptAllMobileAppIds(true)
            if (response.data?.blackListMobileAppIds) setBlackListMobileAppIds(true)

            if (response.data?.environment) {
                if (response.data.environment === 'BOTH') {
                    setEnvironmentType(['APP', 'WEB'])
                } else {
                    setEnvironmentType([response.data.environment])
                }
            }
        } catch (e) {
            onRejected(e)
        } finally {
            setPageLoading(false)
        }
    }

    useEffect(() => {
        loadData()
    }, [])

    useEffect(() => {
        const prev = document.body.style.overflowY
        document.body.style.overflowY = 'scroll'
        return () => { document.body.style.overflowY = prev }
    }, [])

    useEffect(() => {
        if (allReportColumnOptions.length === 0) return
        const available = allReportColumnOptions.map(o => o.value)
        const missing = MANDATORY_REPORT_COLUMNS.filter(v => available.includes(v) && !reportColumns.includes(v))
        if (missing.length) setReportColumns(prev => [...new Set([...prev, ...missing])])
    }, [reportColumns, allReportColumnOptions])

    const onRejected = err => {
        setError(truncateErrorMessage(err?.error))
    }

    const handleValidation = (value) => {
        setValidation(value.target.checked)
    }

    // ─── Domain handlers ────────────────────────────────────────────────────
    const handleAddDomain = (value: string) => setDomains(prev => [...prev, value])
    const handleUploadDomains = (file: File) => {
        const reader = new FileReader()
        reader.onloadend = () => {
            const lines = (reader.result as string).split('\n').map(l => l.trim()).filter(Boolean)
            setDomains(prev => {
                const existingLower = new Set(prev.map(d => d.toLowerCase()))
                return [...prev, ...lines.filter(d => !existingLower.has(d.toLowerCase()))]
            })
        }
        reader.readAsText(file)
    }
    const handleRemoveDomain = (index: number) =>
        setDomains(prev => prev.filter((_, i) => i !== index))

    const handleClearAllDomains = () => {
        Modal.confirm({
            title: 'Clear all domains?',
            content: 'This cannot be undone.',
            okText: 'Clear All',
            okButtonProps: { danger: true },
            cancelText: 'Cancel',
            onOk: () => setDomains([]),
        })
    }

    // ─── Mobile App ID handlers ─────────────────────────────────────────────
    const handleAddMobileAppId = (value: string) => setMobileAppIds(prev => [...prev, value])
    const handleUploadMobileAppIds = (file: File) => {
        const reader = new FileReader()
        reader.onloadend = () => {
            const lines = (reader.result as string).split('\n').map(l => l.trim()).filter(Boolean)
            setMobileAppIds(prev => {
                const existingLower = new Set(prev.map(d => d.toLowerCase()))
                return [...prev, ...lines.filter(d => !existingLower.has(d.toLowerCase()))]
            })
        }
        reader.readAsText(file)
    }
    const handleRemoveMobileAppId = (index: number) =>
        setMobileAppIds(prev => prev.filter((_, i) => i !== index))

    const handleClearAllMobileAppIds = () => {
        Modal.confirm({
            title: 'Clear all mobile app IDs?',
            content: 'This cannot be undone.',
            okText: 'Clear All',
            okButtonProps: { danger: true },
            cancelText: 'Cancel',
            onOk: () => setMobileAppIds([]),
        })
    }

    const selectableReportColumns = allReportColumnOptions.filter(o => o.value !== 'Impressions' && o.value !== 'Wins')
    const mandatoryReportColumns = selectableReportColumns.filter(o => MANDATORY_REPORT_COLUMNS.includes(o.value)).map(o => o.value)
    const allReportColumnsSelected = selectableReportColumns.length > 0 && selectableReportColumns.every(o => reportColumns.includes(o.value))
    const reportColumnsValue = allReportColumnsSelected ? ['All', ...reportColumns] : reportColumns

    const handleReportColumnsChange = (value: string[]) => {
        if (value.includes('All') && !allReportColumnsSelected) {
            setReportColumns(selectableReportColumns.map(o => o.value))
        } else if (!value.includes('All') && allReportColumnsSelected) {
            setReportColumns([...mandatoryReportColumns])
        } else {
            const next = value.filter(v => v !== 'All')
            setReportColumns([...new Set([...next, ...mandatoryReportColumns])])
        }
    }

    return (
        <Spin spinning={pageLoading} size="large" style={{ minHeight: 400 }}>
        <div>
            {error ? <Alert message={error} type="error" style={{ marginBottom: 16 }} /> : null}
            <div className="dsp-sticky-header">
                <Button
                    type="default"
                    icon={<ArrowLeftOutlined onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />}
                    htmlType="button"
                    onClick={navigateToDspList}
                >
                    Go Back
                </Button>
                <DspTabs tabs={dspTabs} activeTab={activeTab} onTabChange={(value) => setActiveTab(value as DspTabKey)} />
                <div className="dsp-header-actions">
                    <ChangeLogHistoryModal
                        entityType="DSP"
                        entityId={Number(id)}
                        fieldLabelMap={DSP_FIELD_LABELS}
                        excludeFields={DSP_EXCLUDED_FIELDS as unknown as string[]}
                        buttonClassName="dsp-cancel-btn"
                    />
                    <Button htmlType="button" onClick={navigateToDspList} className="dsp-cancel-btn">Cancel</Button>
                    <Button htmlType="button" onClick={handleSubmit} className="dsp-submit-btn">Save</Button>
                </div>
            </div>

            <Form
                style={{ maxWidth: '1000px' }}
                onFinish={handleSubmit}
                form={form}
                labelCol={{ span: 7 }}
                wrapperCol={{ span: 17 }}
            >
                <BasicDetails active={activeTab === 'basic'}>
                <FormItem
                    name="name"
                    rules={[
                        { required: true, message: 'Please input DSP name' },
                        { max: 255, message: 'Name cannot be longer that 255 characters' }
                    ]}
                    label="Name"
                    tooltip={'Name of the dsp'}
                >
                    <Input />
                </FormItem>
                <FormItem
                    label="Cluster Endpoints"
                    required
                    tooltip={'DSP endpoint URL per cluster region'}
                    style={{ alignItems: 'flex-start' }}
                    labelCol={{ span: 7, style: { paddingTop: '2px' } }}
                >
                    <div>
                        {clusters.map((cluster: ClusterKeyValue, idx: number) => (
                            <div key={cluster.key}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, padding: '0px 0', width: '100%' }}>
                                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                        <span style={{ width: 110, flexShrink: 0, fontSize: 13, color: 'rgba(0,0,0,0.85)' }}>
                                            {cluster.value}
                                        </span>
                                        <FormItem
                                            name={`cluster_${cluster.key}`}
                                            noStyle
                                            rules={[
                                                ...(defaultCluster === cluster.key
                                                    ? [{ required: true, message: 'Please input the endpoint URL for the default cluster' }]
                                                    : []),
                                                { max: 255, message: 'URL cannot be longer than 255 characters' },
                                            ]}
                                        >
                                            <Input style={{ flex: 1 }} onChange={e => handleClusterUrlChange(cluster.key, e.target.value)} />
                                        </FormItem>
                                        <Button
                                            type="default"
                                            disabled={!clusterUrlValues[cluster.key]}
                                            onClick={() => setDefaultCluster(cluster.key)}
                                            className="dsp-cluster-default-btn"
                                            style={defaultCluster === cluster.key && clusterUrlValues[cluster.key] ? {
                                                backgroundColor: '#EBF3FF',
                                                borderColor: '#1A73E8',
                                                color: '#1A73E8',
                                            } : {}}
                                        >
                                            {defaultCluster === cluster.key && clusterUrlValues[cluster.key] ? 'Default' : 'Set Default'}
                                        </Button>
                                    </div>
                                    <div
                                        className="dsp-cluster-default-text"
                                        style={{ visibility: defaultCluster === cluster.key && clusterUrlValues[cluster.key] ? 'visible' : 'hidden' }}
                                    >
                                        This endpoint applies as the default across all regions
                                    </div>
                                </div>
                                {idx < clusters.length - 1 && (
                                    <div style={{ borderTop: '1px solid #f0f0f0', margin: '4px 0' }} />
                                )}
                            </div>
                        ))}
                    </div>
                </FormItem>
                <FormItem name="dspType" label="DSP Type" rules={[{ required: true, message: 'Please select DSP Type' }]}>
                    <Select placeholder="Select DSP Type">
                        {Object.keys(DspBean.DspTypeEnum).map((type) => <Option key={type} value={type}>{type}</Option>)}
                    </Select>
                </FormItem>
                <FormItem
                    name="tmax"
                    rules={[
                        { required: true, message: 'Please input Timeout' },
                        { type: 'integer', min: 1, max: 10000, message: 'Please input a valid number' }
                    ]}
                    label="Timeout"
                    tooltip={'Maximum wait time for the answer from dsp in milliseconds 1-10000'}
                >
                    <InputNumber min={1} max={10000} className="dsp-number-input" />
                </FormItem>
                <FormItem name="maxQps" label="QPS" tooltip={'Maximum queries per second allowed'}>
                    <InputNumber min={1} max={10000} className="dsp-number-input" />
                </FormItem>
                <FormItem name="gzipCompression" label="Gzip compression" valuePropName="checked" initialValue={true} tooltip={'If true than gzip compression will be used'}>
                    <Checkbox />
                </FormItem>
                <FormItem name="miniExchange" label="Mini Exchange" valuePropName="checked" initialValue={miniExchange} hidden tooltip={'If true than it will be considered as Mini Exchange'}>
                    <Checkbox />
                </FormItem>
                <FormItem required label="DSP Fee" tooltip="DSP Fee by Media Type">
                    <div className="dsp-fee-row">
                        {FEE_CONFIG_DSP.map((item, index) => (
                            <div key={item.mediaType} className="dsp-fee-col">
                                <div className="dsp-fee-label">
                                    {item.mediaType !== 'CTV' && <span style={{ color: '#ff4d4f' }}>* </span>}{item.label}
                                </div>
                                <div className="dsp-fee-input-row">
                                    <FormItem
                                        name={['dspFee', index, 'fee']}
                                        initialValue={0}
                                        rules={item.mediaType !== 'CTV' ? [{ required: true, message: `${item.label} fee is required` }] : []}
                                        style={{ margin: 0 }}
                                        validateTrigger={['onChange', 'onBlur', 'onSubmit']}
                                    >
                                        <InputNumber step={0.01} min={0} max={99} style={{ width: '100%' }} />
                                    </FormItem>
                                    <span style={{ lineHeight: '32px' }}>%</span>
                                    <FormItem name={['dspFee', index, 'mediaType']} initialValue={item.mediaType} hidden style={{ margin: 0 }}>
                                        <Input />
                                    </FormItem>
                                    <FormItem name={['dspFee', index, 'id']} hidden style={{ margin: 0 }}>
                                        <InputNumber />
                                    </FormItem>
                                </div>
                            </div>
                        ))}
                    </div>
                </FormItem>
                <FormItem name="winNotification" label="Win notification">
                    <Select allowClear mode="multiple" placeholder="Select win notification type">
                        {Object.values(WinNotificationEnum).map(win => <Option key={win} value={win}>{win}</Option>)}
                    </Select>
                </FormItem>
                <FormItem name="userSyncEndpoint" rules={[{ max: 255, message: 'Url cannot be longer that 255 characters' }]} label="Cookie sync URL" tooltip={'DSP cookie sync endpoint URL'}>
                    <Input />
                </FormItem>
                <FormItem name="userSyncUidMacro" rules={[{ max: 255, message: 'Macro cannot be longer that 255 characters' }]} label="Cookie sync UID macro" tooltip={'DSP cookie sync user ID macro'}>
                    <Input />
                </FormItem>
                <FormItem name="validation" label="Validation" valuePropName="checked" tooltip={'If true dsp should send signed response only'}>
                    <Checkbox onChange={handleValidation} />
                </FormItem>
                <FormItem name="publicValidationKey" rules={[{ max: 255, message: 'Public validation key cannot be longer that 255 characters' }]} label="Public validation key" tooltip={'Public validation key need to validate dsp response'}>
                    <Input disabled={!validation} />
                </FormItem>
                <FormItem name="active" label="Active" valuePropName="checked" tooltip={'If true than the requests will be sent to dsp'}>
                    <Checkbox />
                </FormItem>
                <FormItem name="email" rules={[{ required: true, message: 'Please input Dsp email' }, { pattern: new RegExp('.+@.+\\..+'), message: 'Email is incorrect' }]} label="Email">
                    <Input />
                </FormItem>
                <FormItem name="timezoneId" rules={[{ required: true, message: 'Please select Time Zone', type: 'string' }]} label="Time Zone">
                    <Select showSearch placeholder="Select time zone">
                        {info.timezones.map((timezone: TimeZoneBean) => <Option key={timezone.id} value={timezone.id as string}>{timezone.id}</Option>)}
                    </Select>
                </FormItem>
                <FormItem required label="Validations limit" tooltip="Limit the number of validation report">
                    <FormItem name="athenaValidationsLimit" rules={[{ required: true, message: 'Limit is required' }]} className="ant-item ant-item-first">
                        <InputNumber min={0} defaultValue={0} className="dsp-number-input" />
                    </FormItem>
                    <FormItem className="ant-item ant-item-second">Validations remaining in the current period: {form.getFieldValue('athenaValidationsLeft')}</FormItem>
                </FormItem>
                <FormItem required label="Request/Response Id period limit" tooltip="Limit of period for Request/Response Id report in hours">
                    <FormItem name="athenaPeriodLimit" rules={[{ required: true, message: 'Limit is required' }]} className="ant-item ant-item-first">
                        <InputNumber min={0} defaultValue={0} className="dsp-number-input" />
                    </FormItem>
                    <FormItem className="ant-item ant-item-second">Report period remaining in the current period: {form.getFieldValue('athenaPeriodLeft')}</FormItem>
                    <FormItem className="ant-item ant-item-second">hours</FormItem>
                </FormItem>
                </BasicDetails>
            </Form>

            {/* ── FILTERING OPTIONS TAB — accordions outside Form ── */}
            <FilteringOptions active={activeTab === 'filtering'}>
                <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
                    <DomainListCard
                        title="Web Domains"
                        subtitle="Manage allowlist & blocklist entries"
                        items={domains}
                        acceptAll={acceptAllDomains}
                        blackList={blackListDomains}
                        collapsed={domainsCollapsed}
                        onToggleCollapse={() => setDomainsCollapsed(v => !v)}
                        onAcceptAllChange={checked => { setAcceptAllDomains(checked); if (checked) setDomains([]) }}
                        onBlackListChange={setBlackListDomains}
                        onAdd={handleAddDomain}
                        onUpload={handleUploadDomains}
                        onRemoveItem={handleRemoveDomain}
                        onClearAll={handleClearAllDomains}
                        initialFileLabel={domainsFileLabel}
                    />
                    <DomainListCard
                        title="MobileApp ID"
                        subtitle="Manage allowlist & blocklist entries"
                        items={mobileAppIds}
                        acceptAll={acceptAllMobileAppIds}
                        blackList={blackListMobileAppIds}
                        collapsed={mobileAppsCollapsed}
                        onToggleCollapse={() => setMobileAppsCollapsed(v => !v)}
                        onAcceptAllChange={checked => { setAcceptAllMobileAppIds(checked); if (checked) setMobileAppIds([]) }}
                        onBlackListChange={setBlackListMobileAppIds}
                        onAdd={handleAddMobileAppId}
                        onUpload={handleUploadMobileAppIds}
                        onRemoveItem={handleRemoveMobileAppId}
                        onClearAll={handleClearAllMobileAppIds}
                        initialFileLabel={mobileAppIdsFileLabel}
                    />
                </div>
                <Form style={{ maxWidth: '1000px' }} form={form} labelCol={{ span: 7 }} wrapperCol={{ span: 17 }}>
                    <FormItem
                        label="Environment"
                        required
                        tooltip={'Select the environment(s) for the DSP'}
                        validateStatus={environmentError ? 'error' : undefined}
                        help={environmentError || undefined}
                    >
                        <Checkbox.Group
                            value={environmentType}
                            onChange={(val: any) => { setEnvironmentType(val); if (val.length > 0) setEnvironmentError('') }}
                            options={[
                                { label: 'Web', value: 'WEB' },
                                { label: 'App', value: 'APP' },
                            ]}
                        />
                    </FormItem>
                    <FormItem name="targetCountries" label="Countries" tooltip="Geo countries for the DSP">
                        <Select mode="multiple" showSearch allowClear placeholder="Select Countries" optionFilterProp="children" onSelect={handleTargetCountrySelect} onDeselect={handleTargetCountryDeselect} filterOption={(input, option) => (option?.children as unknown as string)?.toLowerCase().indexOf(input.toLowerCase()) >= 0}>
                            <Option key="All" value="All">All</Option>
                            {info.countries.map((country: CountryBean) => (
                                <Option key={country.iso3 ?? country.id} title={country.name} value={(country.iso3 ?? '') as string} disabled={!country.iso3}>{country.name}</Option>
                            ))}
                        </Select>
                    </FormItem>
                    <FormItem name="targetDeviceTypes" label="Device Targeting" tooltip="Device types targeted by the DSP">
                        <Select mode="multiple" allowClear showSearch placeholder="Select Device Targeting" optionFilterProp="children" onSelect={handleAllOptionSelect('targetDeviceTypes', info.targetDeviceTypes.map((o) => o.value))} onDeselect={handleAllOptionDeselect('targetDeviceTypes')} filterOption={(input, option) => (option?.children as unknown as string)?.toLowerCase().indexOf(input.toLowerCase()) >= 0}>
                            <Option key="All" value="All">All</Option>
                            {info.targetDeviceTypes.map((opt) => <Option key={opt.value} value={opt.value}>{opt.name}</Option>)}
                        </Select>
                    </FormItem>
                    <FormItem name="targetOperatingSystems" label="OS Targeting" tooltip="Operating systems targeted by the DSP">
                        <Select mode="multiple" allowClear showSearch placeholder="Select OS Targeting" optionFilterProp="children" onSelect={handleAllOptionSelect('targetOperatingSystems', info.targetOperatingSystems.map((o) => o.value))} onDeselect={handleAllOptionDeselect('targetOperatingSystems')} filterOption={(input, option) => (option?.children as unknown as string)?.toLowerCase().indexOf(input.toLowerCase()) >= 0}>
                            <Option key="All" value="All">All</Option>
                            {info.targetOperatingSystems.map((opt) => <Option key={opt.value} value={opt.value}>{opt.name}</Option>)}
                        </Select>
                    </FormItem>
                    <FormItem
                        name="iabCategoryIds"
                        label="IAB Categories"
                        tooltip={'IAB categories allowed for this DSP'}
                    >
                        <TreeSelect
                            allowClear
                            treeCheckable
                            showCheckedStrategy="SHOW_ALL"
                            style={{ maxHeight: '400px', overflow: 'auto' }}
                            placement={'topRight'}
                            placeholder="Select IAB categories"
                            onChange={onChangeIab}
                            treeData={getIabCategoriesAsTree(info.iabCategories as any)}
                            treeNodeFilterProp="title"
                        />
                    </FormItem>
                </Form>
            </FilteringOptions>

            <DashboardConfigure active={activeTab === 'dashboard'}>
                <div className="dsp-dashboard-section">
                    <div className="dsp-dashboard-block">
                        <div className="dsp-section-title">Dashboard KPI</div>
                        <div className="dsp-kpi-subtitle">Controls the KPI card on the chart on the DSP dashboard.</div>
                        <div className="kpi-checkbox-group">
                            <Checkbox.Group
                                value={dashboardKpi}
                                onChange={(val) => {
                                    const newKpi = val as string[]
                                    setDashboardKpi(newKpi)
                                    setReportColumns(prev => {
                                        const kpiValues = new Set(kpiOptions.map(o => o.value))
                                        const base = prev.filter(v => !kpiValues.has(v))
                                        return [...new Set([...base, ...newKpi])]
                                    })
                                }}
                                style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, width: '100%' }}
                            >
                                {kpiOptions.map(opt => (
                                    <Checkbox key={opt.value} value={opt.value} className="dsp-checkbox-chip" style={{ background: dashboardKpi.includes(opt.value) ? '#D4D4D8' : '#fff' }}>
                                        {opt.label}
                                    </Checkbox>
                                ))}
                            </Checkbox.Group>
                        </div>
                        <div className="dsp-kpi-radio-row">
                            <span style={{ color: '#ff4d4f' }}>*</span>
                            <span style={{ color: 'rgba(0,0,0,0.85)', fontSize: 14 }}>Choose an option</span>
                            <Tooltip title="Select the primary metric for your KPI">
                                <InfoCircleOutlined style={{ color: 'rgba(0,0,0,0.45)', fontSize: 12 }} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
                            </Tooltip>
                            <span style={{ color: 'rgba(0,0,0,0.85)', fontSize: 14 }}>:</span>
                            <Radio.Group value={kpiMetric} onChange={e => setKpiMetric(e.target.value)}>
                                <Radio value="Impression">Impression</Radio>
                                <Radio value="Wins">Wins</Radio>
                            </Radio.Group>
                        </div>
                    </div>
                    <Divider />
                    <div className="dsp-dashboard-block">
                        <div className="dsp-section-title" style={{ marginBottom: 12 }}>
                            Report Columns
                            <span className="dsp-report-columns-count">
                                ({reportColumns.length}/{selectableReportColumns.length} enabled)
                            </span>
                        </div>
                        <Select
                            className="report-columns-select"
                            mode="multiple"
                            showSearch
                            allowClear
                            style={{ width: '100%' }}
                            placeholder="Select report columns"
                            optionFilterProp="children"
                            value={reportColumnsValue}
                            onChange={handleReportColumnsChange}
                            filterOption={(input, option) =>
                                (option?.children as unknown as string)?.toLowerCase().indexOf(input.toLowerCase()) >= 0
                            }
                        >
                            <Option key="All" value="All">Select All</Option>
                            {selectableReportColumns.map(opt => (
                                <Option key={opt.value} value={opt.value} disabled={MANDATORY_REPORT_COLUMNS.includes(opt.value)}>{opt.label}</Option>
                            ))}
                        </Select>
                    </div>
                    <Divider />
                    <div>
                        <div className="dsp-section-title" style={{ marginBottom: 8 }}>Api Access</div>
                        <div className="dsp-api-access-header">
                            <div className="dsp-api-access-row">
                                <Switch checked={apiAccess} onChange={setApiAccess} />
                                <span style={{ color: 'rgba(0, 0, 0, 0.85)' }}>API access is {apiAccess ? 'enabled' : 'disabled'}</span>
                            </div>
                            {apiAccess && (
                                <Button type="primary" style={{ borderRadius: 6 }} onClick={handleGenerateApi}>Generate API</Button>
                            )}
                        </div>
                        {apiAccess && (
                            <div className="dsp-api-fields-row">
                                <div className="dsp-api-field">
                                    <div className="dsp-api-field-label">
                                        <span style={{ color: '#ff4d4f' }}>*</span>
                                        <span style={{ color: 'rgba(0,0,0,0.85)' }}>API URL</span>
                                        <Tooltip title="The API endpoint URL for this DSP">
                                            <InfoCircleOutlined style={{ color: 'rgba(0,0,0,0.45)', fontSize: 12 }} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
                                        </Tooltip>
                                        <span style={{ color: 'rgba(0,0,0,0.85)' }}>:</span>
                                    </div>
                                    <Input
                                        value={apiUrl}
                                        onChange={e => setApiUrl(e.target.value)}
                                        style={{ height: 40, borderRadius: 6 }}
                                        suffix={
                                            <Tooltip title="Copy">
                                                <span style={{ cursor: 'pointer', color: 'rgba(0,0,0,0.45)' }} onClick={() => navigator.clipboard.writeText(apiUrl)}>⧉</span>
                                            </Tooltip>
                                        }
                                    />
                                </div>
                                <div className="dsp-api-field">
                                    <div className="dsp-api-field-label">
                                        <span style={{ color: '#ff4d4f' }}>*</span>
                                        <span style={{ color: 'rgba(0,0,0,0.85)' }}>API Key</span>
                                        <Tooltip title="The API key for authentication">
                                            <InfoCircleOutlined style={{ color: 'rgba(0,0,0,0.45)', fontSize: 12 }} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
                                        </Tooltip>
                                        <span style={{ color: 'rgba(0,0,0,0.85)' }}>:</span>
                                    </div>
                                    <Input
                                        value={apiKey}
                                        onChange={e => setApiKey(e.target.value)}
                                        style={{ height: 40, borderRadius: 6 }}
                                        suffix={
                                            <Tooltip title="Copy">
                                                <span style={{ cursor: 'pointer', color: 'rgba(0,0,0,0.45)' }} onClick={() => navigator.clipboard.writeText(apiKey)}>⧉</span>
                                            </Tooltip>
                                        }
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </DashboardConfigure>
        </div>
        </Spin>
    )
}

export default DspEdit;